import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Loader2, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../../lib/adminApi';
import { useAdminAuth } from '../../lib/AdminAuthContext';

export default function AdminLogin() {
  const navigate = useNavigate();
  const { login } = useAdminAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [needs2fa, setNeeds2fa] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const body = { email: email.toLowerCase().trim(), password };
      if (needs2fa) body.totp_code = totpCode.trim();
      const data = await adminApi('/admin/auth/login', { method: 'POST', body: JSON.stringify(body) });
      login(data.token, data.admin);
      navigate('/admin');
    } catch (err) {
      // Server returns { detail: { code: 'totp_required', message: ... } } when 2FA is on
      const msg = err.message || '';
      let parsed = null;
      try { parsed = JSON.parse(msg); } catch { /* not JSON */ }
      const isTotpRequired = (parsed && parsed.code === 'totp_required') || msg.includes('totp_required');
      if (isTotpRequired) {
        setNeeds2fa(true);
        toast.message('Enter your 6-digit authenticator code');
      } else {
        toast.error(msg || 'Invalid credentials');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#0D0B12] flex items-center justify-center p-6" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-white flex items-center justify-center shadow-2xl shadow-[#E07A5F]/30 overflow-hidden">
            <img src="/logo.jpg" alt="AfroConnect" className="w-14 h-14 object-contain" />
          </div>
          <h1 className="mt-4 text-3xl font-serif font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>Admin Console</h1>
          <p className="text-white/60 text-sm mt-1">AfroConnect — manage users, submissions & reports</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-[#1A1820] border border-white/10 rounded-2xl p-6 space-y-4" data-testid="admin-login-form">
          <div className="flex items-center bg-[#0D0B12] border border-white/10 rounded-lg px-4 py-3 focus-within:border-[#E07A5F]">
            <Mail className="w-5 h-5 text-white/40" />
            <input type="email" data-testid="admin-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@afroconnect.com" className="flex-1 ml-3 bg-transparent outline-none text-white placeholder-white/30" />
          </div>
          <div className="flex items-center bg-[#0D0B12] border border-white/10 rounded-lg px-4 py-3 focus-within:border-[#E07A5F]">
            <Lock className="w-5 h-5 text-white/40" />
            <input type="password" data-testid="admin-password-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className="flex-1 ml-3 bg-transparent outline-none text-white placeholder-white/30" />
          </div>
          {needs2fa && (
            <div data-testid="admin-totp-row" className="flex items-center bg-[#0D0B12] border border-[#E9C46A]/40 rounded-lg px-4 py-3 focus-within:border-[#E9C46A]">
              <KeyRound className="w-5 h-5 text-[#E9C46A]" />
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength="6"
                autoFocus
                data-testid="admin-totp-input"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="6-digit authenticator code"
                className="flex-1 ml-3 bg-transparent outline-none text-white placeholder-white/30 tracking-widest"
              />
            </div>
          )}
          <button type="submit" disabled={loading} data-testid="admin-login-submit" className="w-full bg-[#C8553D] hover:bg-[#9E3A26] disabled:opacity-60 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (needs2fa ? 'Verify & Sign In' : 'Sign In')}
          </button>
        </form>
        <p className="text-center text-white/40 text-xs mt-6">Same admin account works on web AND mobile.</p>
      </div>
    </div>
  );
}
